/**
 * @fileoverview Public Privacy Policy page served at `/privacy-policy`.
 *
 * Required by the Google Play Store for the Future Protea Android app.
 * Describes the personal data we collect from app + web users, how it is
 * used, who it is shared with, and how users can exercise their rights —
 * including account deletion.
 *
 * Kept deliberately framework-light: a single self-contained page, no
 * route guards, no data fetching, no client state. The same content
 * works for users browsing on a phone, a desktop, and for Play Store
 * reviewers who need to deep-link straight to the canonical URL.
 */

import { Link } from 'react-router-dom'
import { ArrowLeft, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/components/theme-toggle'

const LOGO_SRC = '/images/web_logo.png'
const EFFECTIVE_DATE = 'June 18, 2026'
const LAST_UPDATED = 'June 18, 2026'
const PRIVACY_EMAIL = 'privacy@futureprotea.com'
const SUPPORT_EMAIL = 'support@futureprotea.com'

type Section = { id: string; title: string }

const SECTIONS: Section[] = [
  { id: 'overview', title: '1. Overview' },
  { id: 'scope', title: '2. Who this policy applies to' },
  { id: 'data-we-collect', title: '3. What data we collect' },
  { id: 'how-we-use', title: '4. How we use your data' },
  { id: 'sharing', title: '5. Who we share data with' },
  { id: 'retention', title: '6. Data retention' },
  { id: 'security', title: '7. Security' },
  { id: 'your-rights', title: '8. Your rights — including data deletion' },
  { id: 'deletion', title: '9. How to request account deletion' },
  { id: 'children', title: '10. Children and minors' },
  { id: 'international', title: '11. International data transfers' },
  { id: 'changes', title: '12. Changes to this policy' },
  { id: 'contact', title: '13. Contact us' },
]

export function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 sticky top-0 z-10">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_SRC} alt="Future Protea" className="h-8 w-auto sm:h-9" />
            <span className="sr-only">Future Protea</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm">
              <Link to="/">
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back to home
              </Link>
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:py-14">
        <div className="mb-10 border-b border-border/60 pb-8">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Legal</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">Privacy Policy</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Effective {EFFECTIVE_DATE} &middot; Last updated {LAST_UPDATED}
          </p>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
            This policy explains what personal data Future Protea collects, why we collect it,
            how we use it, and the choices you have — including how to request that we delete
            your account and the data tied to it.
          </p>
        </div>

        <nav aria-label="Table of contents" className="mb-12 rounded-lg border border-border/60 bg-muted/30 p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            On this page
          </h2>
          <ol className="mt-3 grid gap-1.5 text-sm sm:grid-cols-2">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-primary hover:underline">
                  {s.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="prose-policy space-y-12">
          <section id="overview">
            <h2 className="text-2xl font-semibold">1. Overview</h2>
            <p className="mt-3 text-muted-foreground">
              Future Protea ("we", "us", "our") is a cricket-management platform comprising a
              mobile Android application (the "App") and a web admin available at
              futureprotea.com (the "Web Admin"). We are the data controller for the personal
              data described in this policy.
            </p>
            <p className="mt-3 text-muted-foreground">
              We collect only what we need to run live cricket scoring, tournaments, team
              management, and notifications. We do not sell your data to third parties, we do
              not show third-party advertising, and we do not track you across the open web.
            </p>
          </section>

          <section id="scope">
            <h2 className="text-2xl font-semibold">2. Who this policy applies to</h2>
            <p className="mt-3 text-muted-foreground">This policy applies to:</p>
            <ul className="mt-2 list-inside list-disc space-y-1 text-muted-foreground">
              <li>
                Players, coaches, scorers, umpires, tournament organisers, viewers and
                administrators who hold a Future Protea account.
              </li>
              <li>
                Anonymous visitors who browse the public landing page at futureprotea.com or
                follow a public match link.
              </li>
              <li>
                Parents and guardians who manage accounts on behalf of a minor (see Section 10).
              </li>
            </ul>
          </section>

          <section id="data-we-collect">
            <h2 className="text-2xl font-semibold">3. What data we collect</h2>

            <h3 className="mt-5 text-lg font-semibold">3.1 Account and profile data (you provide)</h3>
            <ul className="mt-2 list-inside list-disc space-y-1 text-muted-foreground">
              <li>Name, email address, mobile phone number, and a password (stored hashed with bcrypt, never in plain text).</li>
              <li>Date of birth, role (player, coach, umpire, scorer, organiser, viewer, administrator).</li>
              <li>Cricket profile: playing role (batter / bowler / all-rounder / wicket-keeper), batting and bowling style, jersey number, school or club affiliation.</li>
              <li>Optional profile photo, uploaded by you and stored in Azure Blob Storage.</li>
              <li>
                For registered players: address, city, state, country, postal code, nationality,
                blood group, height, weight, and the name and phone number of a parent, guardian
                or emergency contact.
              </li>
              <li>A unique player identification code automatically generated for each registered player.</li>
            </ul>

            <h3 className="mt-5 text-lg font-semibold">3.2 Cricket and match data</h3>
            <ul className="mt-2 list-inside list-disc space-y-1 text-muted-foreground">
              <li>Teams you create or are part of, and matches you participate in.</li>
              <li>Ball-by-ball scoring records (runs, wickets, extras, dismissal types) for matches you scored or featured in.</li>
              <li>Tournament participation, fixtures, standings and bracket progression.</li>
              <li>Computed career and per-match statistics (batting average, strike rate, economy, wickets, etc.).</li>
            </ul>

            <h3 className="mt-5 text-lg font-semibold">3.3 Device and diagnostic data (collected automatically)</h3>
            <ul className="mt-2 list-inside list-disc space-y-1 text-muted-foreground">
              <li>App version, operating system name and version, device model.</li>
              <li>Crash reports — exception type, stack trace and the screen route at the time of failure.</li>
              <li>Performance metrics — cold-start time, screen-load time, API call durations.</li>
              <li>Session timestamps used to compute daily and monthly active-user counts.</li>
              <li>
                A Firebase Cloud Messaging (FCM) device token used solely to deliver push
                notifications to your device. Tokens unused for 60 days are deleted automatically.
              </li>
            </ul>

            <h3 className="mt-5 text-lg font-semibold">3.4 Web cookies</h3>
            <p className="mt-2 text-muted-foreground">
              The Web Admin sets a single first-party cookie named <code>protea_rt</code> after
              you sign in. It is httpOnly, Secure, scoped to the <code>/api/auth</code> path,
              and used only to refresh your short-lived access token. It is cleared on logout.
              We do not use third-party advertising cookies.
            </p>

            <h3 className="mt-5 text-lg font-semibold">3.5 What we do NOT collect</h3>
            <ul className="mt-2 list-inside list-disc space-y-1 text-muted-foreground">
              <li>Your contacts, SMS messages, microphone, camera images you didn't explicitly upload, or fine location.</li>
              <li>Your browsing history outside the App or Web Admin.</li>
              <li>Health, financial, or biometric data.</li>
            </ul>
          </section>

          <section id="how-we-use">
            <h2 className="text-2xl font-semibold">4. How we use your data</h2>
            <p className="mt-3 text-muted-foreground">We use the data described above to:</p>
            <ul className="mt-2 list-inside list-disc space-y-1 text-muted-foreground">
              <li>Authenticate you and grant access appropriate to your role.</li>
              <li>Run live cricket scoring, tournament management and statistics.</li>
              <li>
                Deliver push notifications about matches you are involved in, role and approval
                changes, and platform announcements. You can disable notifications at any time
                from the in-app notification preferences or your device settings.
              </li>
              <li>
                Compute leaderboards, standings, and career statistics. Some of these are
                visible to other users and to anonymous viewers via public match links.
              </li>
              <li>Diagnose crashes and performance issues so we can improve reliability.</li>
              <li>Detect and prevent abuse (rate limiting, fraud detection, audit logging).</li>
              <li>Comply with our legal obligations.</li>
            </ul>
            <p className="mt-3 text-muted-foreground">
              We rely on your consent (granted when you create an account) and our legitimate
              interest in operating a safe and functional platform as the legal bases for this
              processing.
            </p>
          </section>

          <section id="sharing">
            <h2 className="text-2xl font-semibold">5. Who we share data with</h2>
            <ul className="mt-3 list-inside list-disc space-y-1 text-muted-foreground">
              <li>
                <strong>Other users on the platform.</strong> Public-facing profile fields (name,
                role, team, profile photo), your in-match statistics, scorecards, and tournament
                standings are visible to other Future Protea users. Match scorecards and
                tournament standings are also visible to anonymous visitors via public match
                links.
              </li>
              <li>
                <strong>Google Firebase.</strong> We use Firebase Cloud Messaging to deliver
                push notifications, Firebase Crashlytics for crash reports, Firebase Analytics
                for product analytics, and Firebase Performance Monitoring for performance
                metrics. Data sent to these services is subject to Google's privacy policy.
              </li>
              <li>
                <strong>Microsoft Azure.</strong> Our backend, database, and Blob Storage
                (where profile photos live) run on Microsoft Azure. Azure acts as our
                processor and does not use your data for its own purposes.
              </li>
              <li>
                <strong>Legal authorities.</strong> Where required by law, court order, or to
                protect the rights, property or safety of users or the public.
              </li>
            </ul>
            <p className="mt-3 text-muted-foreground">
              We <strong>do not sell, rent, or trade</strong> your personal data to third
              parties for marketing or any other purpose.
            </p>
          </section>

          <section id="retention">
            <h2 className="text-2xl font-semibold">6. Data retention</h2>
            <ul className="mt-3 list-inside list-disc space-y-1 text-muted-foreground">
              <li>Account and profile data is retained for as long as your account is active.</li>
              <li>
                Match and tournament data is retained indefinitely so that historical
                leaderboards, standings and records remain intact for other participants and
                organisers. When you request deletion (Section 9), your personal identifiers are
                removed from this history; the underlying match facts (overs, runs, wickets) are
                preserved.
              </li>
              <li>Crash reports and performance telemetry are retained for up to 90 days for diagnostics.</li>
              <li>
                FCM push-notification tokens are pruned automatically: tokens not seen in 60 days
                — and tokens revoked for more than 90 days — are deleted.
              </li>
              <li>The web refresh-token cookie expires within 30 days of your last sign-in.</li>
            </ul>
          </section>

          <section id="security">
            <h2 className="text-2xl font-semibold">7. Security</h2>
            <ul className="mt-3 list-inside list-disc space-y-1 text-muted-foreground">
              <li>All traffic between the App, the Web Admin and our servers is encrypted with HTTPS / TLS.</li>
              <li>Passwords are hashed with bcrypt; we never store or transmit them in plain text.</li>
              <li>
                Authentication uses short-lived JWT access tokens plus a long-lived refresh
                token. On web, the refresh token sits in an httpOnly + Secure cookie that
                JavaScript cannot read. On mobile, the access token is stored in
                <em> flutter_secure_storage</em>, which uses the Android Keystore.
              </li>
              <li>Role-based access controls are enforced on every backend endpoint.</li>
              <li>We log administrative actions and authentication events to support audit and intrusion detection.</li>
            </ul>
            <p className="mt-3 text-muted-foreground">
              No system can be made completely secure. If we become aware of a personal-data
              breach that is likely to affect you, we will notify you and the appropriate
              authorities in line with applicable law.
            </p>
          </section>

          <section id="your-rights">
            <h2 className="text-2xl font-semibold">8. Your rights — including data deletion</h2>
            <p className="mt-3 text-muted-foreground">You have the right to:</p>
            <ul className="mt-2 list-inside list-disc space-y-1 text-muted-foreground">
              <li><strong>Access</strong> the personal data we hold about you.</li>
              <li><strong>Correct</strong> inaccurate or incomplete data — most fields are editable directly from your in-app Profile screen.</li>
              <li><strong>Delete</strong> your account and the personal data tied to it (see Section 9).</li>
              <li><strong>Withdraw consent</strong> at any time, without affecting the lawfulness of any processing done before the withdrawal.</li>
              <li>
                <strong>Disable push notifications</strong> from the in-app Notification Bell
                &gt; Notification preferences, or from your device's system notification
                settings.
              </li>
              <li><strong>Lodge a complaint</strong> with your local data-protection regulator if you believe we have not handled your data lawfully.</li>
            </ul>
          </section>

          <section id="deletion">
            <h2 className="text-2xl font-semibold">9. How to request account deletion</h2>
            <p className="mt-3 text-muted-foreground">
              To request deletion of your Future Protea account and the personal data tied to
              it:
            </p>
            <ol className="mt-2 list-inside list-decimal space-y-1 text-muted-foreground">
              <li>
                Send an email to{' '}
                <a href={`mailto:${PRIVACY_EMAIL}?subject=Account%20Deletion%20Request`} className="text-primary hover:underline">
                  {PRIVACY_EMAIL}
                </a>{' '}
                with the subject line <strong>"Account Deletion Request"</strong>.
              </li>
              <li>Send the email <strong>from the email address registered to your Future Protea account</strong> so we can verify the request.</li>
              <li>We will acknowledge your request within 48 hours.</li>
              <li>We will complete deletion within 30 days and confirm by reply email.</li>
            </ol>
            <p className="mt-4 text-muted-foreground">
              <strong>What gets deleted:</strong> your profile (name, email, phone, date of
              birth, address, photo, blood group, parent/guardian contact, all other profile
              fields), your authentication credentials, your stored push-notification tokens,
              your session and device records, and any uploaded media.
            </p>
            <p className="mt-3 text-muted-foreground">
              <strong>What we retain in anonymised form:</strong> the ball-by-ball facts of
              matches you participated in (overs, runs, wickets) remain part of the match's
              scorecard so the result and the contributions of other players are preserved.
              Where your name appears in a historical scorecard or leaderboard, it is replaced
              with <em>"Former player"</em>.
            </p>
            <p className="mt-3 text-muted-foreground">
              <strong>What we may retain for legal reasons:</strong> minimal records required
              to comply with tax, accounting, or dispute-resolution obligations, kept only for
              the period the law requires.
            </p>
          </section>

          <section id="children">
            <h2 className="text-2xl font-semibold">10. Children and minors</h2>
            <p className="mt-3 text-muted-foreground">
              Future Protea is used by amateur and professional cricket programmes, some of
              whose players are under 18. For players under 18 ("minors"), an account may be
              created by a parent, guardian, coach or recognised cricket administrator, and we
              collect only the minimum information needed to organise and score matches
              (typically name, date of birth, team, and a guardian contact). We do not knowingly
              collect personal data from children under 13 without explicit verifiable parental
              consent.
            </p>
            <p className="mt-3 text-muted-foreground">
              Parents and guardians may exercise all of the rights described in Sections 8 and
              9 on behalf of a minor account-holder by emailing{' '}
              <a href={`mailto:${PRIVACY_EMAIL}`} className="text-primary hover:underline">
                {PRIVACY_EMAIL}
              </a>
              .
            </p>
          </section>

          <section id="international">
            <h2 className="text-2xl font-semibold">11. International data transfers</h2>
            <p className="mt-3 text-muted-foreground">
              Our backend, database, and Blob Storage are hosted on Microsoft Azure. Static
              assets and the Web Admin are served via a content-delivery network with global
              edge locations. As a result, your personal data may be processed in countries
              other than the one you live in. Where we transfer data outside your jurisdiction,
              we rely on the safeguards offered by our processors (Microsoft Azure, Google
              Firebase) under their published cross-border-transfer mechanisms.
            </p>
          </section>

          <section id="changes">
            <h2 className="text-2xl font-semibold">12. Changes to this policy</h2>
            <p className="mt-3 text-muted-foreground">
              We may update this policy from time to time as the product evolves or as the law
              requires. The current version is always published at this URL with an updated
              "Effective" date. For material changes we will also notify you via an in-app
              notification or banner.
            </p>
          </section>

          <section id="contact">
            <h2 className="text-2xl font-semibold">13. Contact us</h2>
            <p className="mt-3 text-muted-foreground">
              For any question about this policy or to exercise any of the rights described
              above:
            </p>
            <div className="mt-4 grid gap-3 rounded-lg border border-border/60 bg-muted/30 p-5 sm:grid-cols-2">
              <div className="flex items-start gap-3">
                <Mail className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm font-semibold">Privacy & deletion requests</p>
                  <a href={`mailto:${PRIVACY_EMAIL}`} className="text-sm text-primary hover:underline">
                    {PRIVACY_EMAIL}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm font-semibold">General support</p>
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sm text-primary hover:underline">
                    {SUPPORT_EMAIL}
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <footer className="border-t border-border/60 bg-muted/30 py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-4 text-sm text-muted-foreground sm:flex-row sm:px-6">
          <p>&copy; {new Date().getFullYear()} Future Protea. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <Link to="/login" className="hover:text-foreground">Admin login</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default PrivacyPolicyPage
